import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  ChevronDown,
  User,
  Settings,
  LogOut,
  ChevronRight,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { env } from "../../../../../config/env";
// ===============================
// COMPONENT
// ===============================

const ProfileMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayUser = useMemo(() => {
    if (!user) {
      return {
        name: "Utilisateur",
        email: "",
        avatar: "/assets/logo/favicon.png",
      };
    }

    return {
      name: `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim(),
      email: user.email ?? "",
      avatar: user.profile_photo_path
        ? `${env.IMAGE_BASE_URL}${user.profile_photo_path}`
        : "/assets/logo/favicon.png",
    };
  }, [user]);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate("/auth/login");
  };

  return (
    <div className="relative ml-2" ref={menuRef}>
      {/* TRIGGER */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-2 py-1 rounded-xl bg-gradient-to-r from-primary/20 to-secondary/20 text-white shadow hover:shadow-lg transition-all border border-white/10"
      >
        <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-primary to-secondary">
          <img
            src={displayUser.avatar}
            alt={displayUser.name}
            className="w-full h-full object-cover  object-top"
          />
        </div>

        <ChevronDown
          size={14}
          className={`text-primary transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* MENU */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute right-0 mt-2 w-64 rounded-2xl bg-dark/95 backdrop-blur-md border border-white/10 shadow-2xl overflow-hidden z-50"
          >
            {/* HEADER */}
            <div className="px-4 py-3 border-b border-white/10">
              <p className="text-white font-semibold">{displayUser.name}</p>
              <p className="text-xs text-primary">{displayUser.email}</p>
            </div>

            {/* ITEMS */}
            <div className="py-2">
              <MenuItem
                icon={<User size={16} />}
                label="Mon profil"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/user/dashboard/profile");
                }}
              />

              <MenuItem
                icon={<Settings size={16} />}
                label="Paramètres"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/user/dashboard/security");
                }}
              />

              <div className="my-1 border-t border-white/10" />

              <MenuItem
                icon={<LogOut size={16} />}
                label="Déconnexion"
                onClick={handleLogout}
                isDanger
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
// ===============================
// MENU ITEM
// ===============================

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isDanger?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({
  icon,
  label,
  onClick,
  isDanger,
}) => {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-all ${isDanger
          ? "text-red-400 hover:bg-red-500/10"
          : "text-gray-200 hover:bg-primary/10"
        }`}
    >
      <div className="flex items-center gap-3">
        {icon}
        <span>{label}</span>
      </div>

      <ChevronRight size={14} className="text-gray-500" />
    </button>
  );
};

export default ProfileMenu;